import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Typography, IconButton, Tooltip, Switch } from '@mui/material';

import { snackbar } from 'src/components/common/snackbar/Snackbar';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import {
	SITE_LOADING_KEY,
	updateSite,
} from 'src/features/site/store/siteThunks';
import { SiteAccessTable } from 'src/features/site_access/components/SiteAccessTable';
import { AppDispatch, RootState } from 'src/store';

const SiteSettingsPage: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const isUpdating = useSelector(
		(state: RootState) => state.loading.loadingStates[SITE_LOADING_KEY],
	);

	const [isAiInferenceEnabled, setIsAiInferenceEnabled] = useState(
		siteDetails?.ai_inference_enabled || false,
	);

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteDetails?.slug}`,
		},
		{ label: 'Site Settings' },
	];

	const handleCopy = () => {
		if (siteDetails?.slug) {
			navigator.clipboard.writeText(siteDetails.slug);
		}
	};

	const handleAiInferenceChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (!siteDetails?.slug || isUpdating) return;
		const newValue = event.target.checked;
		setIsAiInferenceEnabled(newValue);

		dispatch(
			updateSite({
				siteSlug: siteDetails.slug,
				request: { ai_inference_enabled: newValue },
				onSuccess: () => {
					snackbar('Site settings updated successfully', 'success');
				},
				onError: () => {
					setIsAiInferenceEnabled(!newValue);
				},
			}),
		);
	};

	useEffect(() => {
		if (siteDetails) {
			setIsAiInferenceEnabled(siteDetails.ai_inference_enabled || false);
		}
	}, [siteDetails]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box sx={{ p: 0.5 }}>
				<Box
					sx={{
						p: 3,
						mt: 3,
						bgcolor: 'white',
						borderRadius: 1,
						width: '100%',
					}}
				>
					<Typography variant='h6' sx={{ mb: 2, textAlign: 'left' }}>
						Site Settings
					</Typography>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Typography
								variant='body1'
								sx={{ textAlign: 'left', opacity: 0.87 }}
							>
								Site Identifier: {siteDetails?.slug}
							</Typography>
							<Tooltip title='Copy to clipboard'>
								<IconButton
									size='small'
									onClick={handleCopy}
									sx={{ opacity: 0.87 }}
								>
									<ContentCopyIcon fontSize='small' />
								</IconButton>
							</Tooltip>
						</Box>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Switch
								checked={isAiInferenceEnabled}
								onChange={handleAiInferenceChange}
								color='primary'
								disabled={isUpdating}
							/>
							AI Inference Enabled
						</Box>
					</Box>
				</Box>
				<SiteAccessTable siteSlug={siteDetails?.slug || ''} />
			</Box>
		</PageWithLayout>
	);
};

export default SiteSettingsPage;
